import React, { useEffect, useRef, useState } from "react";
import useScrollMotion from "../hooks/useScrollMotion";

/**
 * Photographic palm with three layers of motion:
 *  1) Continuous ambient sway (CSS keyframes, plays even at rest)
 *  2) Vertical parallax drift tied to scroll position
 *  3) Wind-gust tilt tied to scroll velocity (only while in viewport)
 *     — so each palm visibly reacts when you scroll past it.
 *
 * White backgrounds are removed via the global SVG filter `#elio-white-key`.
 */
const WindPalm = ({
  src,
  side = "left",          // "left" | "right" | "center"
  offsetX = -60,
  bottom,
  top,
  width = 420,
  opacity = 1,
  flip = false,
  swayPhase = 0,
  swayDuration = 7,
  scrollIntensity = 1,    // multiplier for the velocity-driven gust
  parallax = 0.12,
  zIndex = 20,
  className = "",
  hideOnMobile = false,
  silhouette = false,     // render as dark silhouette (for over-photo placements)
}) => {
  const { y, velocity } = useScrollMotion();
  const wrapRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [pulse, setPulse] = useState(0); // small one-shot kick when entering view

  // Observe visibility so we only animate when on-screen (perf + intent)
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          // One-shot "wake up" kick when palm first appears
          setPulse(1);
          setTimeout(() => setPulse(0), 900);
        } else {
          setInView(false);
        }
      },
      { rootMargin: "120px 0px 120px 0px", threshold: 0.05 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Velocity-driven gust angle (only when in view), clamped to ±20deg
  const velAngle = inView ? Math.max(-20, Math.min(20, velocity * 0.9 * scrollIntensity)) : 0;
  // A small enter-pulse — palm leans then springs back
  const pulseAngle = pulse * (side === "right" ? -10 : 10);
  // Long-period scroll-based oscillation (always visible, not just on velocity)
  const driftAngle = Math.sin(y / 220) * 5 * scrollIntensity;
  const totalAngle = velAngle + pulseAngle + driftAngle;
  const parallaxY = -y * parallax;

  const sideStyle =
    side === "left"
      ? { left: offsetX }
      : side === "right"
      ? { right: offsetX }
      : { left: "50%", marginLeft: -width / 2 };

  const verticalStyle =
    top !== undefined ? { top } : { bottom: bottom !== undefined ? bottom : -40 };

  return (
    <div
      ref={wrapRef}
      aria-hidden="true"
      className={`absolute pointer-events-none ${hideOnMobile ? "hidden md:block" : ""} ${className}`}
      style={{
        ...sideStyle,
        ...verticalStyle,
        width,
        zIndex,
        transform: `translate3d(0, ${parallaxY}px, 0)`,
        willChange: "transform",
      }}
    >
      {/* Layer 1: scroll-velocity gust (smoothed via transition) */}
      <div
        style={{
          transformOrigin: "bottom center",
          transform: `rotate(${totalAngle}deg)`,
          transition: "transform 0.55s cubic-bezier(.22,.7,.2,1)",
          willChange: "transform",
        }}
      >
        {/* Layer 2: continuous ambient wind sway */}
        <div
          className="wind-ambient"
          style={{
            transformOrigin: "bottom center",
            animationDelay: `${swayPhase}s`,
            animationDuration: `${swayDuration}s`,
          }}
        >
          {/* Layer 3: optional flip */}
          <div style={{ transform: flip ? "scaleX(-1)" : undefined }}>
            <img
              src={src}
              alt=""
              draggable="false"
              loading="lazy"
              className="block w-full h-auto select-none"
              style={{
                opacity,
                filter: silhouette ? "url(#elio-silhouette)" : "url(#elio-white-key)",
                userSelect: "none",
                WebkitUserDrag: "none",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default WindPalm;
